/**
 * gift-cart-guard.js — Cygnuss
 *
 * Protecao contra cliente finalizar compra apenas com brinde no carrinho.
 *
 * Versao SIMPLIFICADA (2026-05-05): apos serie de regressoes na transicao
 * cliente <-> CartPanda (drawer branco, items somindo, botao disabled em
 * carrinho com items via bfcache), todo o codigo de pageshow handler,
 * applySections, button-disable e click-interceptor foi removido. O backup
 * "Backup Testes 01-07" demonstrou que NAO ter essas camadas mantem a
 * transicao CartPanda funcionando bem — Dawn sozinho lida com o ciclo.
 *
 * O QUE PERMANECE — Camada 1 (Auto-remove brinde sem qualificador):
 *   - Subscribe ao cartUpdate pubsub
 *   - Quando cart so tem brindes, /cart/change.js zera quantidade do brinde
 *   - O proximo render natural do Dawn reflete o cart sem o brinde
 *   - Cliente fica com cart vazio se tinha so brinde — Shopify/CartPanda
 *     ja bloqueiam checkout em cart vazio naturalmente, sem precisar mexer
 *     no botao via JS.
 *
 * Identificacao do brinde: item.properties._is_gift === 'true'
 * (gerado pelo free-gift-engine.js — NAO modificar).
 */
(function () {
  'use strict';

  var DEBUG = false;
  var SOURCE_TAG = 'gift-cart-guard';
  var TOAST_TEXT_REMOVED = 'Brinde removido — adicione um produto qualificador';

  // Anti-loop: o proprio /cart/change.js dispara cartUpdate. Mesmo padrao
  // do cart-upsell.js (setTimeout 0 para macrotask).
  var isGuardProcessing = false;

  // Debounce — multiplos cartUpdates em rajada coalescem num unico fetch.
  var RECONCILE_DEBOUNCE_MS = 100;
  var reconcileTimer = null;

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[gift-guard]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  // -------------------- Helpers --------------------

  function isGiftItem(item) {
    return item && item.properties && item.properties._is_gift === 'true';
  }

  function cartHasOnlyGifts(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    var qualifiers = 0;
    var gifts = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftItem(cart.items[i])) gifts++;
      else qualifiers++;
    }
    return qualifiers === 0 && gifts > 0;
  }

  async function fetchCart() {
    try {
      var res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Accept': 'application/json' },
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      log('fetchCart fail', e);
      return null;
    }
  }

  // -------------------- Toast --------------------

  function showToast(text) {
    try {
      var el = document.createElement('div');
      el.textContent = text;
      el.style.cssText = [
        'position:fixed', 'bottom:20px', 'left:50%',
        'transform:translateX(-50%)', 'background:#1e1e1e',
        'color:#fff', 'padding:10px 16px', 'border-radius:8px',
        'font-size:13px', 'z-index:99999', 'box-shadow:0 4px 12px rgba(0,0,0,.3)',
        'opacity:0', 'transition:opacity .2s ease',
      ].join(';');
      document.body.appendChild(el);
      requestAnimationFrame(function () { el.style.opacity = '1'; });
      setTimeout(function () {
        el.style.opacity = '0';
        setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 250);
      }, 3500);
    } catch (e) {}
  }

  // -------------------- Camada 1: remover brinde --------------------
  //
  // /cart/change.js retorna o cart inteiro. Sem `sections:` no body, NAO
  // forcamos render concorrente — Dawn cart-drawer.js renderContents
  // (acionado pelo cartUpdate pubsub) reflete o estado fresco naturalmente.
  // Isso evita race com a propria pubsub chain.

  async function removeGiftItem(key) {
    try {
      var res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Gift-Guard-Source': SOURCE_TAG,
        },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      return res.ok ? await res.json() : null;
    } catch (e) {
      log('removeGiftItem fail', e);
      return null;
    }
  }

  async function removeAllGifts() {
    if (isGuardProcessing) return;
    isGuardProcessing = true;
    try {
      var cart = await fetchCart();
      if (!cart || !cart.items || cart.items.length === 0) return;

      var giftKeys = cart.items.filter(isGiftItem).map(function (it) { return it.key; });
      if (giftKeys.length === 0) return;

      // SAFETY: nunca remove se cart tem qualificadores. Snapshot stale
      // de cartUpdate em rajada poderia trazer aqui falsamente.
      var nonGifts = cart.items.filter(function (it) { return !isGiftItem(it); });
      if (nonGifts.length > 0) {
        log('removeAllGifts aborted — cart has', nonGifts.length, 'non-gift item(s)');
        return;
      }

      // Remocao sequencial — cada /cart/change.js retorna cart consistente.
      var lastCart = null;
      for (var i = 0; i < giftKeys.length; i++) {
        lastCart = await removeGiftItem(giftKeys[i]);
      }

      // Publica cartUpdate com source = SOURCE_TAG pra outros listeners
      // nao retriggerem este guard (anti-loop). Dawn cart.js subscriber
      // vai re-renderizar o drawer naturalmente.
      try {
        if (window.publish && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: SOURCE_TAG,
            cartData: lastCart,
          });
        }
      } catch (e) {}

      // Dispara um "cart:refresh" pra cart-drawer/cart.js refazerem o
      // render — sem isso, o drawer ficaria sem o brinde no servidor mas
      // ainda visualmente presente ate proxima interacao.
      try {
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          detail: { source: SOURCE_TAG },
        }));
      } catch (e) {}

      showToast(TOAST_TEXT_REMOVED);
      log('removed', giftKeys.length, 'gift(s)');
    } finally {
      // setTimeout(0): proximo cartUpdate auto-induzido cai no guard
      // antes da flag voltar a false.
      setTimeout(function () { isGuardProcessing = false; }, 0);
    }
  }

  // -------------------- Reconciliacao --------------------

  async function reconcileNow() {
    if (isGuardProcessing) return;
    var cart = await fetchCart();
    if (!cart || !cart.items || cart.items.length === 0) return;

    if (cartHasOnlyGifts(cart)) {
      // SAFETY: re-fetch apos pequena espera. Race entre cart-items qty
      // change + free-gift-engine.applyDiff pode deixar snapshot transitorio
      // de "so brinde". Re-fetch garante estado pos-mutacoes.
      await new Promise(function (resolve) { setTimeout(resolve, 50); });
      var verify = await fetchCart();
      if (!verify || !verify.items || verify.items.length === 0) return;
      if (!cartHasOnlyGifts(verify)) return;

      await removeAllGifts();
    }
  }

  function reconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      reconcileNow();
    }, RECONCILE_DEBOUNCE_MS);
  }

  // -------------------- Wire up --------------------

  function init() {
    // Subscribe ao pubsub do tema. Mesmo padrao do free-gift-engine.
    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.source === SOURCE_TAG) return; // ignora self
        setTimeout(reconcile, 0);
      });
    }

    // Listener defensivo pra eventos DOM comuns.
    ['cart:updated', 'cart:refresh', 'cart-drawer:updated'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        if (e && e.detail && e.detail.source === SOURCE_TAG) return;
        setTimeout(reconcile, 0);
      });
    });

    // Boot: cobre cenario de pagina carregada com cart so-brinde
    // (do localStorage/cookie do Shopify pre-existente).
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', reconcile);
    } else {
      reconcile();
    }
  }

  init();
})();
